// Event Processor Service - Processes events from the events table and creates notifications

import { supabase } from '../utils/supabase';
import { createLogger } from '../utils/logger';

const logger = createLogger({ module: 'EventProcessorService', level: 'info' });

interface Event {
  id: string;
  event_type: string;
  event_version: number;
  aggregate_id: string;
  aggregate_type: string;
  user_id: string;
  event_data: Record<string, any>;
  metadata: Record<string, any>;
  created_at: string;
  processed_at?: string;
  correlation_id?: string;
  causation_id?: string;
}

interface NotificationTemplate {
  id: string;
  key: string;
  title_template: string;
  message_template: string;
  notification_type: string;
  category: string;
  priority: string;
  delivery_method: string[];
  action_url_template?: string;
  action_text_template?: string;
  active: boolean;
  conditions?: Record<string, any>;
}

class EventProcessorService {
  private static instance: EventProcessorService;
  private isProcessing = false;

  private constructor() {}

  public static getInstance(): EventProcessorService {
    if (!EventProcessorService.instance) {
      EventProcessorService.instance = new EventProcessorService();
    }
    return EventProcessorService.instance;
  }

  public async processUnprocessedEvents(): Promise<void> {
    if (this.isProcessing) {
      logger.debug('Event processing already in progress, skipping');
      return;
    }

    // Check if we have an authenticated session
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      logger.debug('No authenticated session, skipping event processing');
      return;
    }

    this.isProcessing = true;

    try {
      logger.debug('Starting event processing...');

      // Get unprocessed events
      const { data: events, error } = await supabase
        .from('events')
        .select('*')
        .is('processed_at', null)
        .order('created_at', { ascending: true })
        .limit(50); // Process in batches

      if (error) {
        throw new Error(`Failed to fetch unprocessed events: ${error.message}`);
      }

      if (!events || events.length === 0) {
        logger.debug('No unprocessed events found');
        return;
      }

      logger.info(`Processing ${events.length} unprocessed events`);

      // Process each event
      for (const event of events) {
        await this.processEvent(event);
      }

      logger.info(`Completed processing ${events.length} events`);
    } catch (error) {
      logger.error('Error processing events:', error);
    } finally {
      this.isProcessing = false;
    }
  }

  private async processEvent(event: Event): Promise<void> {
    try {
      logger.debug(`Processing event: ${event.event_type} (${event.id})`);

      // Get matching notification templates
      const templates = await this.getMatchingTemplates(event.event_type);

      if (templates.length === 0) {
        logger.debug(`No templates found for event type: ${event.event_type}`);
        await this.markEventAsProcessed(event.id);
        return;
      }

      // Get user information for template variables
      const user = await this.getUserInfo(event.user_id);
      if (!user) {
        logger.warn(`User not found for event: ${event.id}`);
        await this.markEventAsProcessed(event.id);
        return;
      }

      // Prepare template variables
      const templateVariables = {
        first_name: user.first_name || 'there',
        last_name: user.last_name || '',
        email: user.email || '',
        ...event.event_data
      };

      // Process each matching template
      for (const template of templates) {
        if (await this.shouldCreateNotification(template, event, user)) {
          await this.createNotificationFromTemplate(template, event.user_id, templateVariables);
        }
      }

      // Mark event as processed
      await this.markEventAsProcessed(event.id);

      logger.debug(`Successfully processed event: ${event.id}`);
    } catch (error) {
      logger.error(`Error processing event ${event.id}:`, error);
      // Don't mark as processed if there was an error - will retry later
    }
  }

  private async getMatchingTemplates(eventType: string): Promise<NotificationTemplate[]> {
    try {
      const { data: templates, error } = await supabase
        .from('notification_templates')
        .select('*')
        .eq('active', true);

      if (error) {
        throw new Error(`Failed to fetch templates: ${error.message}`);
      }

      if (!templates) {
        return [];
      }

      // Filter templates that match this event type
      return templates.filter(template => this.shouldProcessTemplate(template, eventType));
    } catch (error) {
      logger.error('Error fetching templates:', error);
      return [];
    }
  }

  private shouldProcessTemplate(template: NotificationTemplate, eventType: string): boolean {
    // Map event types to template keys
    const eventTemplateMap: Record<string, string[]> = {
      'user.registered': ['welcome_new_user'],
      'achievement.unlocked': ['achievement_unlocked'],
      'level.up': ['level_up'],
      'streak.milestone': ['streak_milestone'],
      'card.created': ['first_card_created', 'card_milestone'],
      'note.created': ['first_note_created'],
      'review.completed': ['review_completed']
    };

    const applicableTemplates = eventTemplateMap[eventType] || [];
    return applicableTemplates.includes(template.key);
  }

  private async getUserInfo(userId: string): Promise<any> {
    try {
      const { data: user, error } = await supabase
        .from('users')
        .select('first_name, last_name, email')
        .eq('id', userId)
        .single();

      if (error) {
        logger.error('Error fetching user data:', error);
        return null;
      }

      return user;
    } catch (error) {
      logger.error('Error fetching user info:', error);
      return null;
    }
  }

  private async shouldCreateNotification(
    template: NotificationTemplate, 
    event: Event, 
    user: any
  ): Promise<boolean> {
    try {
      // Check template conditions if any
      if (template.conditions) {
        // Add custom logic here for template conditions
        // For now, we'll create notifications for all matching templates
      }

      // Check if this is a milestone notification (e.g., first card, first note)
      if (template.key === 'first_card_created') {
        return await this.isFirstCard(event.user_id);
      }

      if (template.key === 'first_note_created') {
        return await this.isFirstNote(event.user_id);
      }

      // For other templates, create notification
      return true;
    } catch (error) {
      logger.error('Error checking notification conditions:', error);
      return false;
    }
  }

  private async isFirstCard(userId: string): Promise<boolean> {
    try {
      const { count, error } = await supabase
        .from('career_cards')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);

      if (error) {
        logger.error('Error checking card count:', error);
        return false;
      }

      return (count || 0) === 1; // First card if count is 1
    } catch (error) {
      logger.error('Error checking if first card:', error);
      return false;
    }
  }

  private async isFirstNote(userId: string): Promise<boolean> {
    try {
      const { count, error } = await supabase
        .from('notes')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId);

      if (error) {
        logger.error('Error checking note count:', error);
        return false;
      }

      return (count || 0) === 1; // First note if count is 1
    } catch (error) {
      logger.error('Error checking if first note:', error);
      return false;
    }
  }

  private async createNotificationFromTemplate(
    template: NotificationTemplate, 
    userId: string, 
    variables: Record<string, any>
  ): Promise<void> {
    try {
      // Process template variables
      const title = this.processTemplate(template.title_template, variables);
      const message = this.processTemplate(template.message_template, variables);
      const actionUrl = template.action_url_template 
        ? this.processTemplate(template.action_url_template, variables)
        : null;
      const actionText = template.action_text_template 
        ? this.processTemplate(template.action_text_template, variables)
        : null;

      // Create the notification
      const { error } = await supabase
        .from('notifications')
        .insert({
          user_id: userId,
          title,
          message,
          notification_type: template.notification_type,
          category: template.category,
          priority: template.priority,
          delivery_method: Array.isArray(template.delivery_method) 
            ? template.delivery_method[0] 
            : template.delivery_method, // Handle both array and string formats
          action_url: actionUrl,
          action_text: actionText,
          metadata: {
            template_key: template.key,
            ...variables
          },
          created_at: new Date().toISOString()
        });

      if (error) {
        throw new Error(`Failed to create notification: ${error.message}`);
      }

      logger.debug(`Notification created from template ${template.key} for user ${userId}`);
    } catch (error) {
      logger.error(`Error creating notification from template ${template.key}:`, error);
      throw error;
    }
  }

  private processTemplate(template: string, variables: Record<string, any>): string {
    let processed = template;
    
    // Replace {{variable}} placeholders
    Object.entries(variables).forEach(([key, value]) => {
      const placeholder = `{{${key}}}`;
      processed = processed.replace(new RegExp(placeholder, 'g'), String(value || ''));
    });

    return processed;
  }

  private async markEventAsProcessed(eventId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('events')
        .update({ processed_at: new Date().toISOString() })
        .eq('id', eventId);

      if (error) {
        throw new Error(`Failed to mark event as processed: ${error.message}`);
      }
    } catch (error) {
      logger.error(`Error marking event ${eventId} as processed:`, error);
      throw error;
    }
  }
}

// Export singleton instance
export const eventProcessor = EventProcessorService.getInstance();

// Helper function to start event processing
export const processEvents = async (): Promise<void> => {
  await eventProcessor.processUnprocessedEvents();
}; 